import { useRef, useState, useEffect } from "react";
import api from "@/lib/api";
import { toast } from "sonner";
import { Camera, Eraser, X } from "lucide-react";

/* Photo uploader — uploads to object storage, returns file ids */
export function PhotoUploader({ photos, onChange, purpose = "job-photo" }) {
  const ref = useRef();
  const [busy, setBusy] = useState(false);
  const add = async (e) => {
    const files = Array.from(e.target.files || []);
    if (!files.length) return;
    setBusy(true);
    const ids = [...photos];
    for (const f of files) {
      const fd = new FormData();
      fd.append("file", f);
      try { const { data } = await api.post(`/files/upload?purpose=${purpose}`, fd); ids.push(data.id); }
      catch { toast.error("Upload failed"); }
    }
    onChange(ids);
    setBusy(false);
  };
  return (
    <div>
      <div className="flex flex-wrap gap-2">
        {photos.map((id) => <ImgThumb key={id} id={id} onRemove={() => onChange(photos.filter((x) => x !== id))} />)}
        <button type="button" onClick={() => ref.current?.click()} data-testid="job-add-photo"
          className="h-16 w-16 grid place-items-center rounded-lg border-2 border-dashed border-slate-300 text-slate-400 hover:border-blue-400 hover:text-blue-accent">
          {busy ? "…" : <Camera size={20} />}
        </button>
      </div>
      <input ref={ref} type="file" accept="image/*" multiple capture="environment" onChange={add} className="hidden" />
    </div>
  );
}

function ImgThumb({ id, onRemove }) {
  const [url, setUrl] = useState(null);
  useEffect(() => {
    let active = true;
    api.get(`/files/${id}/download`, { responseType: "blob" }).then((r) => {
      if (active) setUrl(URL.createObjectURL(r.data));
    }).catch(() => {});
    return () => { active = false; if (url) URL.revokeObjectURL(url); };
  }, [id]); // eslint-disable-line
  return (
    <div className="relative h-16 w-16 rounded-lg overflow-hidden border border-slate-200 bg-slate-100">
      {url && <img src={url} alt="" className="h-full w-full object-cover" />}
      <button type="button" onClick={onRemove} className="absolute top-0 right-0 bg-black/50 text-white rounded-bl p-0.5"><X size={12} /></button>
    </div>
  );
}

/* Signature pad — captures a signature and uploads it, returns file id */
export function SignaturePad({ value, onChange }) {
  const canvasRef = useRef();
  const drawing = useRef(false);
  const [saved, setSaved] = useState(!!value);

  const pos = (e) => {
    const c = canvasRef.current; const r = c.getBoundingClientRect();
    const t = e.touches ? e.touches[0] : e;
    return { x: (t.clientX - r.left) * (c.width / r.width), y: (t.clientY - r.top) * (c.height / r.height) };
  };
  const start = (e) => { drawing.current = true; const ctx = canvasRef.current.getContext("2d"); const p = pos(e); ctx.beginPath(); ctx.moveTo(p.x, p.y); };
  const move = (e) => {
    if (!drawing.current) return;
    e.preventDefault();
    const ctx = canvasRef.current.getContext("2d");
    const p = pos(e); ctx.lineTo(p.x, p.y); ctx.strokeStyle = "#0F172A"; ctx.lineWidth = 2; ctx.stroke();
  };
  const end = () => { drawing.current = false; };
  const clear = () => { const c = canvasRef.current; c.getContext("2d").clearRect(0, 0, c.width, c.height); setSaved(false); onChange(null); };
  const save = async () => {
    const blob = await new Promise((res) => canvasRef.current.toBlob(res, "image/png"));
    const fd = new FormData(); fd.append("file", new File([blob], "signature.png", { type: "image/png" }));
    try { const { data } = await api.post("/files/upload?purpose=signature", fd); onChange(data.id); setSaved(true); toast.success("Signature captured"); }
    catch { toast.error("Could not save signature"); }
  };

  return (
    <div>
      <canvas ref={canvasRef} width={480} height={140} data-testid="signature-canvas"
        onMouseDown={start} onMouseMove={move} onMouseUp={end} onMouseLeave={end}
        onTouchStart={start} onTouchMove={move} onTouchEnd={end}
        className="w-full h-32 rounded-lg border border-slate-300 bg-white touch-none" />
      <div className="flex gap-2 mt-2">
        <button type="button" onClick={clear} className="text-sm px-3 py-1.5 rounded-lg border border-slate-300 flex items-center gap-1"><Eraser size={14} /> Clear</button>
        <button type="button" onClick={save} data-testid="signature-save" className="text-sm px-3 py-1.5 rounded-lg bg-blue-600 text-white">Save signature</button>
        {saved && <span className="text-sm text-emerald-600 self-center">✓ Captured</span>}
      </div>
    </div>
  );
}
